import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../ui/table';
import {
    AlertCircle,
    TrendingUp,
    TrendingDown,
    DollarSign,
    BarChart3,
    RefreshCw,
    Calendar,
} from 'lucide-react';
import reportService from '../../services/reportService';
import cashBoxService, { CashBox } from '../../services/cashBoxService';
import { DailyBalanceDTO, DailyBalanceParams } from '../../types';
import CalendarModal from '../CalendarModal';
import { showError } from '../Alerts';

interface CashBoxSummaryModalProps {
    isOpen: boolean;
    onClose: () => void;
    cashBoxId: number | null;
}

export default function CashBoxSummaryModal({ isOpen, onClose, cashBoxId }: CashBoxSummaryModalProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [cashBoxes, setCashBoxes] = useState<CashBox[]>([]);
    const [dailyData, setDailyData] = useState<DailyBalanceDTO[]>([]);

    const [params, setParams] = useState<DailyBalanceParams>({ cashBoxId: 0, startDate: '', endDate: '' });
    const [desdeModalOpen, setDesdeModalOpen] = useState(false);
    const [hastaModalOpen, setHastaModalOpen] = useState(false);

    useEffect(() => {
        if (!isOpen || cashBoxId === null) return;
        const load = async () => {
            try {
                const boxes = await cashBoxService.getAllCashBoxes();
                setCashBoxes(boxes);
                const today = new Date();
                const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                setParams({
                    cashBoxId,
                    startDate: firstDay.toISOString().split('T')[0],
                    endDate: lastDay.toISOString().split('T')[0],
                });
            } catch {
                showError('Error al cargar la información de la caja');
            }
        };
        load();
    }, [isOpen, cashBoxId]);

    useEffect(() => {
        if (!isOpen) { setDailyData([]); setError(null); }
    }, [isOpen]);

    useEffect(() => {
        if (!params.cashBoxId || !params.startDate || !params.endDate) return;
        const fetch = async () => {
            setError(null);
            setLoading(true);
            try {
                const data = await reportService.getDailyBalance(params);
                setDailyData(data);
            } catch (err: any) {
                const msg = err.response?.data?.message || 'Error al cargar el reporte.';
                setError(msg);
                showError(msg);
            } finally {
                setLoading(false);
            }
        };
        fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.cashBoxId, params.startDate, params.endDate]);

    const selectedBox = cashBoxes.find((b) => b.id === params.cashBoxId);
    const currency = selectedBox?.currency || 'ARS';
    const currencySymbol = currency === 'USD' ? 'US$' : '$';

    const fmt = (amount: number) =>
        `${currencySymbol} ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const fmtDate = (d: string) =>
        new Date(d + 'T00:00:00').toLocaleDateString('es-AR', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });

    const fmtShort = (d: string) =>
        new Date(d + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });

    const fmtLabel = (d: string) =>
        new Date(d + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });

    const totals = useMemo(() => dailyData.reduce(
        (acc, day) => ({
            totalIncome: acc.totalIncome + day.totalCreditos,
            totalExpense: acc.totalExpense + day.totalDebitos,
            netChange: acc.netChange + day.netChange,
        }),
        { totalIncome: 0, totalExpense: 0, netChange: 0 }
    ), [dailyData]);

    const maxAmount = useMemo(() =>
        Math.max(...dailyData.map((d) => Math.max(d.totalCreditos, d.totalDebitos, 1)), 1),
        [dailyData]
    );

    return (
        <>
            <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
                <DialogContent className="w-[95vw] max-w-4xl bg-card border-border p-0 max-h-[90vh] flex flex-col">

                    {/* Header */}
                    <DialogHeader className="shrink-0 px-5 pt-5 pb-4 border-b border-border">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                <BarChart3 className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <DialogTitle className="text-lg font-bold text-foreground">
                                    Resumen Diario
                                </DialogTitle>
                                <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5">
                                    {selectedBox?.name || 'Cargando...'}
                                    {currency && (
                                        <Badge variant="outline" className="px-1.5 py-0 h-5 text-[10px]">{currency}</Badge>
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Date filters */}
                        <div className="grid grid-cols-2 gap-3 mt-4">
                            <div className="space-y-1.5">
                                <Label className="text-xs font-medium text-muted-foreground">Desde</Label>
                                <Button
                                    variant="outline"
                                    className="w-full justify-start text-left font-normal h-9 rounded-lg bg-background border-border"
                                    onClick={() => setDesdeModalOpen(true)}
                                >
                                    <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                                    {params.startDate ? fmtLabel(params.startDate) : 'Fecha inicial'}
                                </Button>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs font-medium text-muted-foreground">Hasta</Label>
                                <Button
                                    variant="outline"
                                    className="w-full justify-start text-left font-normal h-9 rounded-lg bg-background border-border"
                                    onClick={() => setHastaModalOpen(true)}
                                >
                                    <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                                    {params.endDate ? fmtLabel(params.endDate) : 'Fecha final'}
                                </Button>
                            </div>
                        </div>
                    </DialogHeader>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto p-5 space-y-5">
                        {error && (
                            <div className="flex items-center gap-3 p-4 text-sm rounded-xl bg-destructive/5 border border-destructive/20 text-destructive">
                                <AlertCircle className="w-5 h-5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-24">
                                <RefreshCw className="h-8 w-8 animate-spin text-primary mb-4" />
                                <p className="text-sm text-muted-foreground">Calculando resumen...</p>
                            </div>
                        ) : dailyData.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center">
                                <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
                                    <BarChart3 className="h-8 w-8 text-muted-foreground/40" />
                                </div>
                                <p className="text-base font-semibold text-foreground">Sin datos para este período</p>
                                <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                                    Ajustá el rango de fechas para ver el resumen diario.
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* KPI cards */}
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                    <div className="flex flex-col p-4 rounded-xl border border-border bg-background">
                                        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Total Ingresos</p>
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center shrink-0">
                                                <TrendingUp className="w-4 h-4 text-success" />
                                            </div>
                                            <p className="text-xl font-bold text-success tabular-nums">
                                                +{fmt(totals.totalIncome)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col p-4 rounded-xl border border-border bg-background">
                                        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Total Egresos</p>
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                                                <TrendingDown className="w-4 h-4 text-destructive" />
                                            </div>
                                            <p className="text-xl font-bold text-destructive tabular-nums">
                                                -{fmt(totals.totalExpense)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col p-4 rounded-xl border border-border bg-background">
                                        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Resultado Neto</p>
                                        <div className="flex items-center gap-3">
                                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${totals.netChange >= 0 ? 'bg-info/10' : 'bg-destructive/10'}`}>
                                                <DollarSign className={`w-4 h-4 ${totals.netChange >= 0 ? 'text-info' : 'text-destructive'}`} />
                                            </div>
                                            <p className={`text-xl font-bold tabular-nums ${totals.netChange >= 0 ? 'text-info' : 'text-destructive'}`}>
                                                {totals.netChange >= 0 ? '+' : ''}{fmt(totals.netChange)}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Chart + Table */}
                                <div className="border border-border rounded-xl overflow-hidden bg-card">
                                    {/* Bar chart */}
                                    <div className="bg-muted/30 border-b border-border p-5">
                                        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                                            Rendimiento Diario Comparativo
                                        </p>
                                        <div className="flex items-end gap-1.5 h-20">
                                            {dailyData.map((day, idx) => {
                                                const incomeH = (day.totalCreditos / maxAmount) * 100;
                                                const expenseH = (day.totalDebitos / maxAmount) * 100;
                                                return (
                                                    <div
                                                        key={idx}
                                                        className="flex-1 flex items-end gap-[1px] group relative min-w-0"
                                                        title={`${fmtDate(day.date)} — Ingreso: ${fmt(day.totalCreditos)} | Egreso: ${fmt(day.totalDebitos)}`}
                                                    >
                                                        <div className="flex-1 rounded-t-sm bg-success/60 group-hover:bg-success/80 transition-colors min-h-[3px]" style={{ height: `${Math.max(incomeH, 4)}%` }} />
                                                        <div className="flex-1 rounded-t-sm bg-destructive/60 group-hover:bg-destructive/80 transition-colors min-h-[3px]" style={{ height: `${Math.max(expenseH, 4)}%` }} />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="flex justify-between mt-2 text-[10px] text-muted-foreground font-medium">
                                            {dailyData.length > 0 && <span>{fmtShort(dailyData[0].date)}</span>}
                                            {dailyData.length > 1 && <span>{fmtShort(dailyData[dailyData.length - 1].date)}</span>}
                                        </div>
                                    </div>

                                    {/* Table */}
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="bg-muted/20">
                                                    <TableHead className="py-3">Fecha</TableHead>
                                                    <TableHead className="py-3 text-right">Ingresos</TableHead>
                                                    <TableHead className="py-3 text-right">Egresos</TableHead>
                                                    <TableHead className="py-3 text-right">Neto del Día</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {dailyData.map((day, idx) => (
                                                    <TableRow key={idx} className="hover:bg-muted/30 transition-colors">
                                                        <TableCell className="py-3 font-medium text-foreground">
                                                            {fmtDate(day.date)}
                                                        </TableCell>
                                                        <TableCell className="py-3 text-right text-success font-medium tabular-nums">
                                                            {day.totalCreditos > 0 ? `+${fmt(day.totalCreditos)}` : fmt(0)}
                                                        </TableCell>
                                                        <TableCell className="py-3 text-right text-destructive font-medium tabular-nums">
                                                            {day.totalDebitos > 0 ? `-${fmt(day.totalDebitos)}` : fmt(0)}
                                                        </TableCell>
                                                        <TableCell className="py-3 text-right">
                                                            <Badge
                                                                variant={day.netChange >= 0 ? 'default' : 'destructive'}
                                                                className={`tabular-nums font-semibold px-2 ${day.netChange >= 0 ? 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20' : ''}`}
                                                            >
                                                                {day.netChange >= 0 ? '+' : ''}{fmt(day.netChange)}
                                                            </Badge>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="shrink-0 flex justify-end px-5 py-3 border-t border-border">
                        <Button variant="outline" size="sm" onClick={onClose} className="rounded-lg">
                            Cerrar
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <CalendarModal
                open={desdeModalOpen}
                onOpenChange={setDesdeModalOpen}
                onSelectDate={(date) => setParams({ ...params, startDate: date.toISOString().split('T')[0] })}
                selectedDate={params.startDate ? new Date(params.startDate + 'T00:00:00') : undefined}
            />
            <CalendarModal
                open={hastaModalOpen}
                onOpenChange={setHastaModalOpen}
                onSelectDate={(date) => setParams({ ...params, endDate: date.toISOString().split('T')[0] })}
                selectedDate={params.endDate ? new Date(params.endDate + 'T00:00:00') : undefined}
            />
        </>
    );
}
